import {RelativeTime} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {SidebarDeploymentsProps} from '../types'
import {SidebarSection} from './SidebarSection'
import {SidebarListItem} from './SidebarListItem'
import styles from './SidebarDeployments.module.css'

function getDeploymentStatus(env: {state: string; createdAt?: string}) {
  if (env.state === 'active' && env.createdAt) {
    return <RelativeTime date={new Date(env.createdAt)} tense="past" />
  }
  return <span>{env.state}</span>
}

/** Renders the Deployments section of the repository sidebar. */
export function SidebarDeployments({environments, deploymentsPath}: SidebarDeploymentsProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const totalDeployments = environments.length
  const baseMaxToShow = 3
  const maxToShow =
    totalDeployments === baseMaxToShow + 1 ? totalDeployments : Math.min(totalDeployments, baseMaxToShow)
  const displayed = environments.slice(0, maxToShow)

  const moreCount = totalDeployments - maxToShow

  return (
    <SidebarSection
      title="Deployments"
      href={deploymentsPath}
      count={totalDeployments}
      moreCount={moreCount > 1 ? moreCount : undefined}
      moreLabel="deployments"
    >
      {totalDeployments === 0 ? (
        <div className={styles.emptyState}>
          <span>No deployments published</span>
        </div>
      ) : (
        <ul className={styles.envList}>
          {displayed.map(env => (
            <SidebarListItem
              key={`deploy-${env.name}`}
              className={styles.envItem}
              label={env.name}
              description={getDeploymentStatus(env)}
              href={env.path}
              onClick={() =>
                sendClickAnalyticsEvent({
                  category: 'REPOSITORY_OVERVIEW',
                  action: 'click',
                  label: 'location:sidebar;section:deployments',
                })
              }
            />
          ))}
        </ul>
      )}
    </SidebarSection>
  )
}

export function SidebarDeploymentsSkeleton() {
  return (
    <SidebarSection title="Deployments">
      <SkeletonText />
    </SidebarSection>
  )
}
